import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Calendar, CreditCard, TrendingUp, Cake, ExternalLink, RefreshCw, AlertTriangle, Send, CheckCircle, MessageCircle, Clock3, ChevronDown, ChevronRight, ChevronsDown, ChevronsUp, Activity, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { APP_VERSION } from '@/version';
import FailedReloadAlert from '@/components/FailedReloadAlert';
import SetupBanner from '@/components/dashboard/SetupBanner';
import SystemHealthWidget from '@/components/dashboard/SystemHealthWidget';
import MindbodyHealthBadge from '@/components/dashboard/MindbodyHealthBadge';
import StudioUtilizationCard from '@/components/dashboard/StudioUtilizationCard';
import StudioRevenueCard from '@/components/dashboard/StudioRevenueCard';
import StudioDashboardOverview from '@/components/dashboard/StudioDashboardOverview';
import { NextMonthRevenue } from '@/components/dashboard/NextMonthRevenue';
import EngagementWidget from '@/components/dashboard/EngagementWidget';
import ReferralStatsCard from '@/components/dashboard/ReferralStatsCard';
import AIWeeklyReportWidget from '@/components/dashboard/AIWeeklyReportWidget';
import FailedPaymentsWidget from '@/components/dashboard/FailedPaymentsWidget';
import UnbilledTrainingsWidget from '@/components/dashboard/UnbilledTrainingsWidget';
import WaitingCustomersWidget from '@/components/dashboard/WaitingCustomersWidget';
import RevenueTimelineChart from '@/components/dashboard/RevenueTimelineChart';
import TrainingRevenueCard from '@/components/dashboard/TrainingRevenueCard';
import ReservationsDashboardWidget from '@/components/dashboard/ReservationsDashboardWidget';
import { useTenantBranding } from '@/contexts/TenantBrandingContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Collapsible section wrapper for dashboard widgets
const CollapsibleSection = ({ sectionKey, title, icon, isOpen, onToggle, statusColor = 'green', badge, children }) => {
  const colorMap = {
    green: { dot: 'bg-green-500', border: 'border-green-200', bg: 'hover:bg-green-50/50' },
    orange: { dot: 'bg-amber-500', border: 'border-amber-200', bg: 'hover:bg-amber-50/50' },
    red: { dot: 'bg-red-500', border: 'border-red-200', bg: 'hover:bg-red-50/50' },
    gray: { dot: 'bg-gray-400', border: 'border-gray-200', bg: 'hover:bg-gray-50/50' },
  };
  const colors = colorMap[statusColor] || colorMap.green;

  return (
    <div className={`rounded-lg border ${isOpen ? 'border-gray-200' : colors.border} overflow-hidden transition-all`} data-testid={`section-${sectionKey}`}>
      <button
        onClick={() => onToggle(sectionKey)}
        className={`w-full flex items-center justify-between px-4 py-3 bg-white ${colors.bg} transition-colors`}
        data-testid={`section-toggle-${sectionKey}`}
      >
        <div className="flex items-center gap-3">
          <span className={`w-2.5 h-2.5 rounded-full ${colors.dot} flex-shrink-0`} />
          <span className="text-gray-600">{icon}</span>
          <span className="font-semibold text-sm text-gray-800">{title}</span>
          {badge && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              statusColor === 'red' ? 'bg-red-100 text-red-700' :
              statusColor === 'orange' ? 'bg-amber-100 text-amber-700' :
              'bg-green-100 text-green-700'
            }`}>{badge}</span>
          )}
        </div>
        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>
      {isOpen && (
        <div className="border-t border-gray-100">
          {children}
        </div>
      )}
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { companyName, name: brandName } = useTenantBranding();
  const businessName = companyName || brandName || 'Ihrem Betrieb';
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekAppointments, setWeekAppointments] = useState([]);
  const [monthAppointments, setMonthAppointments] = useState([]);
  const [todayAppointments, setTodayAppointments] = useState([]);
  const [lastAppointment, setLastAppointment] = useState(null);
  const [birthdaysToday, setBirthdaysToday] = useState([]);
  const [newCustomersThisMonth, setNewCustomersThisMonth] = useState(0);
  const [servedCustomersToday, setServedCustomersToday] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [monthRevenue, setMonthRevenue] = useState(0);
  const [whatsappResponseMetrics, setWhatsappResponseMetrics] = useState(null);
  const [aiResponseMetrics, setAiResponseMetrics] = useState(null);
  const [pendingInvoices, setPendingInvoices] = useState(null);
  // Kreditkarten-Ablauf State
  const [expiringCards, setExpiringCards] = useState({ expiring_soon: 0, expired: 0, cards: [] });
  const [sendingCardNotifications, setSendingCardNotifications] = useState(false);

  // Collapsible sections state - all closed by default
  const [openSections, setOpenSections] = useState({});

  const toggleSection = useCallback((key) => {
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const sectionKeys = ['system-health', 'engagement', 'ai-report', 'whatsapp-monitoring', 'ai-monitoring', 'last-appointment'];

  const openAll = useCallback(() => {
    const all = {};
    sectionKeys.forEach(k => { all[k] = true; });
    setOpenSections(all);
  }, []);

  const closeAll = useCallback(() => {
    setOpenSections({});
  }, []);

  const anyOpen = Object.values(openSections).some(Boolean);

  useEffect(() => {
    loadOverview(true);
    fetchPendingInvoices();
  }, []);

  const loadOverview = async (showLoader = false) => {
    if (showLoader) {
      setLoading(true);
    }

    try {
      const response = await axios.get(`${API}/dashboard/overview`);
      const data = response.data || {};
      setStats(data.stats || null);
      setTodayAppointments(data.today_appointments || []);
      setWeekAppointments(data.week_appointments || []);
      setMonthAppointments(data.month_appointments || []);
      setLastAppointment(data.last_appointment || null);
      setBirthdaysToday(data.birthdays_today || []);
      setNewCustomersThisMonth(data.new_customers_month || 0);
      setServedCustomersToday(data.served_customers_today || 0);
      setTodayRevenue(data.today_revenue || 0);
      setMonthRevenue(data.month_revenue || 0);
      setWhatsappResponseMetrics(data.whatsapp_response_metrics || null);
      setAiResponseMetrics(data.ai_response_metrics || null);
      setExpiringCards(data.expiring_cards || { expiring_soon: 0, expired: 0, cards: [] });
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      toast.error('Fehler beim Laden des Dashboards');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const fetchPendingInvoices = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/billing/pending-charges-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingInvoices(response.data);
    } catch (error) {
      console.error('Error fetching pending charges:', error);
    }
  };

  // NEU: Kreditkarten-Benachrichtigungen manuell senden
  const sendCardExpiryNotifications = async () => {
    if (!window.confirm(`Möchten Sie Benachrichtigungen an ${expiringCards.expiring_soon} Kunden mit bald ablaufenden Karten senden?`)) {
      return;
    }
    
    setSendingCardNotifications(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/dashboard/send-card-expiry-notifications`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`✅ ${response.data.sent || 0} Benachrichtigungen gesendet!`);
      loadOverview();
    } catch (error) {
      console.error('Error sending card expiry notifications:', error);
      toast.error('Fehler beim Senden der Benachrichtigungen');
    } finally {
      setSendingCardNotifications(false);
    }
  };

  const runBillingScheduler = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/billing/run-scheduler`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Abrechnungs-Scheduler wurde gestartet');
      // Nachlauf: Pending-Charges neu laden
      await fetchPendingInvoices();
    } catch (error) {
      console.error('Error running billing scheduler:', error);
      const msg = error?.response?.data?.detail || 'Fehler beim Starten des Abrechnungs-Schedulers';
      toast.error(msg);
    }
  };

  const formatResponseTime = (seconds) => {
    if (seconds === null || seconds === undefined) return '—';
    if (seconds < 60) return `${Math.round(seconds)}s`;

    const minutes = Math.floor(seconds / 60);
    const restSeconds = Math.round(seconds % 60);
    return `${minutes}m ${String(restSeconds).padStart(2, '0')}s`;
  };

  const formatResponseMs = (ms) => {
    if (ms === null || ms === undefined) return '—';
    if (ms < 1000) return `${Math.round(ms)}ms`;
    const seconds = ms / 1000;
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return `${minutes}m ${String(rest).padStart(2, '0')}s`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const statCards = [
    {
      title: 'Gesamt Kunden',
      value: stats?.total_customers || 0,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Neue Kunden diesen Monat',
      value: newCustomersThisMonth,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: 'Aktive Abos',
      value: stats?.active_subscriptions || 0,
      icon: CreditCard,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
  ];
  
  // Iter 339+: ALLE Termin- und Umsatz-Karten wurden in die per-Studio-
  // „Standort-Übersicht" verschoben (oberhalb). Nichts mehr in den unteren
  // Reihen — sie werden komplett ausgeblendet.
  const appointmentCards = [];
  const revenueCards = [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Welcome Section */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Willkommen zurück!</h1>
        <p className="text-gray-600" data-testid="dashboard-subtitle">Hier ist eine Übersicht von {businessName}</p>
      </div>

      {/* Betriebs-Onboarding Hinweis (nur wenn Einrichtung offen) */}
      <SetupBanner />

      {/* Failed Reload Alert */}
      <FailedReloadAlert />

      {/* 07/2026: Kunden die auf Antwort warten (Eskalationen + Takeover) */}
      <WaitingCustomersWidget />

      {/* Iter 339+ Standort-Übersicht: pro Studio Termine + Umsatz (heute/Monat) */}
      <StudioDashboardOverview />

      {/* Erwarteter Umsatz nächster Kalendermonat (aus gebuchten Terminen) */}
      <div className="mt-6">
        <NextMonthRevenue />
      </div>

      {/* 07/2026: Umsatz-Verlauf letzte 30 Tage */}
      <RevenueTimelineChart />

      {/* 06/2026: Trainings-Erträge (Ist-Trainings, Punktewert, direkte CHF) */}
      <TrainingRevenueCard />

      {/* Failed Payments Widget (tägliche/Monats-Charge-Failures) */}
      <FailedPaymentsWidget />

      {/* 06/2026: Trainings ohne Abrechnung (Abrechnungs-Kontrolle) */}
      <UnbilledTrainingsWidget />

      {/* Iter 310: Chatbot Slot-Reservation Live-Übersicht.
          Versteckt sich automatisch wenn keine Aktivität, damit der
          Dashboard ohne aktive Reservierungen sauber bleibt. */}
      <ReservationsDashboardWidget />

      {/* Kreditkarten-Ablauf Alert */}
      {(expiringCards.expiring_soon > 0 || expiringCards.expired > 0) && (
        <Card className={`border-2 ${expiringCards.expired > 0 ? 'border-red-300 bg-red-50' : 'border-amber-300 bg-amber-50'}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`${expiringCards.expired > 0 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'} p-3 rounded-full`}>
                  <CreditCard size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Kreditkarten-Status</h3>
                  <div className="flex gap-4 mt-1 text-sm">
                    {expiringCards.expired > 0 && (
                      <span className="text-red-600 font-medium flex items-center gap-1">
                        <AlertTriangle size={14} />
                        {expiringCards.expired} abgelaufen
                      </span>
                    )}
                    {expiringCards.expiring_soon > 0 && (
                      <span className="text-amber-600 font-medium">
                        ⏰ {expiringCards.expiring_soon} laufen bald ab
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                {expiringCards.expiring_soon > 0 && (
                  <Button
                    onClick={sendCardExpiryNotifications}
                    disabled={sendingCardNotifications}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    size="sm"
                  >
                    {sendingCardNotifications ? (
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Erinnerung senden
                  </Button>
                )}
                <Button
                  onClick={() => navigate('/customers?filter=expiring_cards')}
                  variant="outline"
                  size="sm"
                >
                  Details anzeigen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid - 3 cards (removed Umsatz) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="stat-card hover-lift transition-all duration-300 border border-gray-100 hover:border-amber-200/50 hover:shadow-lg" data-testid={`stat-card-${index}`}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{stat.title}</p>
                    <h3 className="text-3xl font-bold mt-2" data-testid={`stat-value-${index}`}>{stat.value}</h3>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-3 rounded-xl`}>
                    <Icon size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Appointment Stats - 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
        {appointmentCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="stat-card hover-lift transition-all duration-300 border border-gray-100 hover:border-amber-200/50 hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{stat.title}</p>
                    <h3 className="text-3xl font-bold mt-2">{stat.value}</h3>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-3 rounded-xl`}>
                    <Icon size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      
      {/* Revenue Stats - 3 cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 stagger-children">
        {revenueCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card key={index} className="stat-card hover-lift transition-all duration-300 border border-gray-100 hover:border-amber-200/50 hover:shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wider">{stat.title}</p>
                    <h3 className="text-3xl font-bold mt-2">{stat.value}</h3>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-3 rounded-xl`}>
                    <Icon size={24} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Geburtstage heute */}
      {birthdaysToday && birthdaysToday.length > 0 && (
        <Card className="bg-pink-50 border-pink-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cake size={24} className="text-pink-600" />
              Geburtstage heute
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {birthdaysToday.map((birthday, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white border border-pink-200 rounded-lg"
                >
                  <div>
                    <p className="font-medium text-pink-800">🎉 {birthday.name}</p>
                    <p className="text-sm text-pink-600">wird heute {birthday.age} Jahre alt!</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Offene Abrechnungen (Ende Monat / taeglich Kunden) */}
      {pendingInvoices && pendingInvoices.total_customers > 0 && (
        <Card className="bg-orange-50 border-orange-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard size={24} className="text-orange-600" />
                Offene Abrechnungen
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={runBillingScheduler}
                className="flex items-center gap-2 border-orange-300 text-orange-700 hover:bg-orange-100"
              >
                <RefreshCw size={16} />
                Abrechnung jetzt ausführen
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="p-4 bg-white border border-orange-200 rounded-lg">
                  <p className="text-2xl font-bold text-orange-800">{pendingInvoices.total_customers}</p>
                  <p className="text-sm text-orange-600">Kunden</p>
                </div>
                <div className="p-4 bg-white border border-orange-200 rounded-lg">
                  <p className="text-2xl font-bold text-orange-800">{pendingInvoices.total_sessions}</p>
                  <p className="text-sm text-orange-600">Trainings</p>
                </div>
                <div className="p-4 bg-white border border-orange-200 rounded-lg">
                  <p className="text-2xl font-bold text-orange-800">CHF {pendingInvoices.total_amount?.toFixed(2)}</p>
                  <p className="text-sm text-orange-600">Gesamt</p>
                </div>
              </div>
              
              {pendingInvoices.top_customers && pendingInvoices.top_customers.length > 0 && (
                <>
                  <p className="text-sm font-semibold text-orange-800 mt-4">Top 5 Kunden:</p>
                  {pendingInvoices.top_customers.map((customer, idx) => (
                    <div key={customer.customer_id} className="flex items-center justify-between p-3 bg-white border border-orange-100 rounded-lg">
                      <div>
                        <p className="font-medium text-orange-800">{customer.customer_name}</p>
                        <p className="text-sm text-orange-600">{customer.sessions} Trainings</p>
                      </div>
                      <p className="font-semibold text-orange-800">CHF {customer.amount.toFixed(2)}</p>
                    </div>
                  ))}
                </>
              )}
              
              {/* Details anzeigen Button */}
              <div className="pt-4 border-t border-orange-200">
                <Button
                  onClick={() => navigate('/billing')}
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  Details anzeigen & Abrechnen
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* === Collapsible Sections Toggle === */}
      <div className="flex items-center justify-end gap-2" data-testid="collapse-controls">
        <Button
          variant="outline"
          size="sm"
          onClick={anyOpen ? closeAll : openAll}
          className="text-xs"
          data-testid="toggle-all-sections-btn"
        >
          {anyOpen ? (
            <><ChevronsUp className="w-3.5 h-3.5 mr-1" /> Alle schliessen</>
          ) : (
            <><ChevronsDown className="w-3.5 h-3.5 mr-1" /> Alle öffnen</>
          )}
        </Button>
      </div>

      {/* System Health Widget - Collapsible */}
      <CollapsibleSection
        sectionKey="system-health"
        title="System Status"
        icon={<Activity className="h-5 w-5" />}
        isOpen={openSections['system-health']}
        onToggle={toggleSection}
        statusColor="green"
      >
        <div className="space-y-3">
          <MindbodyHealthBadge />
          <StudioRevenueCard />
          <StudioUtilizationCard />
          <SystemHealthWidget />
        </div>
      </CollapsibleSection>

      {/* Kunden-Engagement Widget - Collapsible */}
      <CollapsibleSection
        sectionKey="engagement"
        title="Kunden-Engagement"
        icon={<Activity className="h-5 w-5" />}
        isOpen={openSections['engagement']}
        onToggle={toggleSection}
        statusColor="green"
      >
        <EngagementWidget />
      </CollapsibleSection>

      {/* Empfehlungen - Collapsible */}
      <CollapsibleSection
        sectionKey="referrals"
        title="Empfehlungen"
        icon={<Share2 className="h-5 w-5" />}
        isOpen={openSections['referrals']}
        onToggle={toggleSection}
        statusColor="green"
      >
        <ReferralStatsCard />
      </CollapsibleSection>

      {/* KI Performance Report - Collapsible */}
      <CollapsibleSection
        sectionKey="ai-report"
        title="KI Performance Report (7 Tage)"
        icon={<TrendingUp className="h-5 w-5" />}
        isOpen={openSections['ai-report']}
        onToggle={toggleSection}
        statusColor="green"
      >
        <AIWeeklyReportWidget />
      </CollapsibleSection>

      {/* WhatsApp Antwortzeit Monitoring - Collapsible */}
      {whatsappResponseMetrics && (
        <CollapsibleSection
          sectionKey="whatsapp-monitoring"
          title="WhatsApp Antwortzeit-Monitoring (heute)"
          icon={<MessageCircle className="h-5 w-5" />}
          isOpen={openSections['whatsapp-monitoring']}
          onToggle={toggleSection}
          statusColor={
            whatsappResponseMetrics.status_label === 'Kritisch' ? 'red' :
            whatsappResponseMetrics.status_label === 'Langsam' ? 'orange' : 'green'
          }
          badge={whatsappResponseMetrics.status_label}
        >
          <Card className="border-teal-200 bg-teal-50/60" data-testid="dashboard-whatsapp-response-monitoring-card">
            <CardContent className="space-y-4 pt-4" data-testid="dashboard-whatsapp-response-monitoring-content">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-whatsapp-response-monitoring-grid">
                <div className="p-3 rounded-lg bg-white border border-teal-200" data-testid="dashboard-whatsapp-response-monitoring-avg-card">
                  <p className="text-xs text-teal-700">Ø Antwortzeit</p>
                  <p className="text-xl font-bold text-teal-900" data-testid="dashboard-whatsapp-response-monitoring-avg-value">
                    {formatResponseTime(whatsappResponseMetrics.avg_response_seconds)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-teal-200" data-testid="dashboard-whatsapp-response-monitoring-p90-card">
                  <p className="text-xs text-teal-700">P90 Antwortzeit</p>
                  <p className="text-xl font-bold text-teal-900" data-testid="dashboard-whatsapp-response-monitoring-p90-value">
                    {formatResponseTime(whatsappResponseMetrics.p90_response_seconds)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-teal-200" data-testid="dashboard-whatsapp-response-monitoring-count-card">
                  <p className="text-xs text-teal-700">Bot-Antworten</p>
                  <p className="text-xl font-bold text-teal-900" data-testid="dashboard-whatsapp-response-monitoring-count-value">
                    {whatsappResponseMetrics.responses_count || 0}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-teal-200" data-testid="dashboard-whatsapp-response-monitoring-open-card">
                  <p className="text-xs text-teal-700">Offene Eingänge</p>
                  <p className="text-xl font-bold text-teal-900" data-testid="dashboard-whatsapp-response-monitoring-open-value">
                    {whatsappResponseMetrics.open_incoming_count || 0}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-teal-800" data-testid="dashboard-whatsapp-response-monitoring-footer">
                <div className="flex items-center gap-2" data-testid="dashboard-whatsapp-response-monitoring-profile">
                  <Clock3 className="w-4 h-4" />
                  Aktives Speed-Profil: <strong>{whatsappResponseMetrics.selected_speed_profile || 'balanced'}</strong>
                </div>
                <Button
                  variant="outline"
                  className="border-teal-300 text-teal-800 hover:bg-teal-100"
                  onClick={() => navigate('/whatsapp')}
                  data-testid="dashboard-whatsapp-response-monitoring-open-chat-button"
                >
                  Zum WhatsApp-Chat
                </Button>
              </div>
            </CardContent>
          </Card>
        </CollapsibleSection>
      )}

      {/* KI Antwortzeit Monitoring - Collapsible */}
      {aiResponseMetrics && (
        <CollapsibleSection
          sectionKey="ai-monitoring"
          title="KI Antwortzeit-Monitoring (24h)"
          icon={<Clock3 className="h-5 w-5" />}
          isOpen={openSections['ai-monitoring']}
          onToggle={toggleSection}
          statusColor={
            (aiResponseMetrics.success_rate || 0) < 80 ? 'red' :
            (aiResponseMetrics.success_rate || 0) < 95 ? 'orange' : 'green'
          }
          badge={aiResponseMetrics.status_label || 'Keine Daten'}
        >
          <Card className="border-indigo-200 bg-indigo-50/60" data-testid="dashboard-ai-response-monitoring-card">
            <CardContent className="space-y-4 pt-4" data-testid="dashboard-ai-response-monitoring-content">
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="dashboard-ai-response-monitoring-grid">
                <div className="p-3 rounded-lg bg-white border border-indigo-200" data-testid="dashboard-ai-response-monitoring-avg-card">
                  <p className="text-xs text-indigo-700">Ø Antwortzeit</p>
                  <p className="text-xl font-bold text-indigo-900" data-testid="dashboard-ai-response-monitoring-avg-value">
                    {formatResponseMs(aiResponseMetrics.avg_ms)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-indigo-200" data-testid="dashboard-ai-response-monitoring-p90-card">
                  <p className="text-xs text-indigo-700">P90 Antwortzeit</p>
                  <p className="text-xl font-bold text-indigo-900" data-testid="dashboard-ai-response-monitoring-p90-value">
                    {formatResponseMs(aiResponseMetrics.p90_ms)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-indigo-200" data-testid="dashboard-ai-response-monitoring-p95-card">
                  <p className="text-xs text-indigo-700">P95 Antwortzeit</p>
                  <p className="text-xl font-bold text-indigo-900" data-testid="dashboard-ai-response-monitoring-p95-value">
                    {formatResponseMs(aiResponseMetrics.p95_ms)}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-indigo-200" data-testid="dashboard-ai-response-monitoring-total-card">
                  <p className="text-xs text-indigo-700">KI-Requests</p>
                  <p className="text-xl font-bold text-indigo-900" data-testid="dashboard-ai-response-monitoring-total-value">
                    {aiResponseMetrics.total_requests || 0}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-white border border-indigo-200" data-testid="dashboard-ai-response-monitoring-success-card">
                  <p className="text-xs text-indigo-700">Erfolgsrate</p>
                  <p className="text-xl font-bold text-indigo-900" data-testid="dashboard-ai-response-monitoring-success-value">
                    {aiResponseMetrics.success_rate || 0}%
                  </p>
                </div>
              </div>
              {aiResponseMetrics.flow_summary && (
                <div className="flex items-center gap-4 text-xs px-1" data-testid="dashboard-ai-flow-summary">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Schnell (&lt;3s): <strong>{aiResponseMetrics.flow_summary.fast_count}</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    Mittel (3-10s): <strong>{aiResponseMetrics.flow_summary.medium_count}</strong>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-full bg-rose-500" />
                    Langsam (&gt;10s): <strong>{aiResponseMetrics.flow_summary.slow_count}</strong>
                  </span>
                </div>
              )}
              {(aiResponseMetrics.recommendations || []).length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="dashboard-ai-recommendations">
                  <p className="text-sm font-semibold text-amber-800 mb-2">Empfehlungen</p>
                  <div className="space-y-1.5">
                    {aiResponseMetrics.recommendations.map((rec, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs text-amber-900" data-testid={`dashboard-ai-recommendation-${i}`}>
                        <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${rec.severity === 'high' ? 'bg-rose-500' : 'bg-amber-500'}`} />
                        <span>{rec.message}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="p-3 bg-white border border-indigo-200 rounded-lg" data-testid="dashboard-ai-response-monitoring-slowest-flows">
                <p className="text-sm font-semibold text-indigo-800 mb-2">Langsamste KI-Flows</p>
                {(aiResponseMetrics.slowest_flows || []).length > 0 ? (
                  <div className="space-y-2">
                    {aiResponseMetrics.slowest_flows.slice(0, 5).map((flowItem) => (
                      <div key={flowItem.flow} className="flex items-center justify-between text-sm" data-testid={`dashboard-ai-response-flow-${flowItem.flow}`}>
                        <span className="text-indigo-900 truncate mr-2">{flowItem.flow}</span>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${flowItem.success_rate >= 95 ? 'bg-emerald-100 text-emerald-700' : flowItem.success_rate >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-rose-100 text-rose-700'}`}>
                            {flowItem.success_rate}%
                          </span>
                          <span className="font-medium text-indigo-800">{formatResponseMs(flowItem.avg_ms)} · {flowItem.count}x</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-indigo-700" data-testid="dashboard-ai-response-monitoring-empty">Noch keine KI-Metriken vorhanden.</p>
                )}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-indigo-800" data-testid="dashboard-ai-response-monitoring-footer">
                <div className="flex items-center gap-2" data-testid="dashboard-ai-response-monitoring-profiles">
                  <TrendingUp className="w-4 h-4" />
                  Profile: Chat <strong>{aiResponseMetrics.active_profiles?.ai_chat_profile || 'balanced'}</strong> · Voice <strong>{aiResponseMetrics.active_profiles?.nutrition_voice_speed_profile || 'balanced'}</strong>
                </div>
                <Button
                  variant="outline"
                  className="border-indigo-300 text-indigo-800 hover:bg-indigo-100"
                  onClick={() => navigate('/settings/advanced?tab=ki-api')}
                  data-testid="dashboard-ai-response-monitoring-settings-button"
                >
                  KI-Profile anpassen
                </Button>
              </div>
            </CardContent>
          </Card>
        </CollapsibleSection>
      )}

      {/* Letzter Termin - Collapsible */}
      <CollapsibleSection
        sectionKey="last-appointment"
        title="Letzter Termin im Kalender"
        icon={<Calendar className="h-5 w-5" />}
        isOpen={openSections['last-appointment']}
        onToggle={toggleSection}
        statusColor={lastAppointment ? 'green' : 'gray'}
      >
        <Card>
          <CardContent className="pt-4">
            {lastAppointment ? (
              <div className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                <div>
                  <p className="font-medium">
                    {lastAppointment.service_name || 'Unbekannter Service'}
                    {lastAppointment.selected_variant && (
                      <span className="ml-2 text-sm text-blue-600">({lastAppointment.selected_variant})</span>
                    )}
                  </p>
                  <p className="text-sm text-gray-600">{lastAppointment.customer_name || 'Unbekannter Kunde'}</p>
                  <p className="text-xs text-gray-500">
                    {new Date(lastAppointment.start_time).toLocaleString('de-DE', { 
                      day: '2-digit', 
                      month: '2-digit', 
                      year: 'numeric',
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  lastAppointment.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                }`}>
                  {lastAppointment.status}
                </span>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">Keine Termine vorhanden</p>
            )}
          </CardContent>
        </Card>
      </CollapsibleSection>
    </div>
  );
};

export default DashboardPage;
